import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
} from '@mui/material'
import { RequestWorker } from '../../types/Work/Request'
import React, { useState } from 'react'

interface AssignToDialogProps {
    open: boolean
    onClose: () => void
    assignees: RequestWorker[]
    selectedCount: number
    onAssign: (assigneeId: number) => Promise<void>
    selectedItems: any[]
}

export default function AssignToDialog({
    open,
    onClose,
    assignees,
    selectedCount,
    onAssign,
    selectedItems,
}: AssignToDialogProps) {
    const [selectedAssignee, setSelectedAssignee] = useState<number | ''>('')
    const [isAssigning, setIsAssigning] = useState(false)

    const handleAssign = async () => {
        if (selectedAssignee) {
            setIsAssigning(true)
            try {
                await onAssign(selectedAssignee)
                setSelectedAssignee('')
                onClose()
            } finally {
                setIsAssigning(false)
            }
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Assign Items</DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Assign {selectedCount} selected item{selectedCount !== 1 ? 's' : ''} to:
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="assignee-select-label">Assignee</InputLabel>
                        <Select
                            labelId="assignee-select-label"
                            value={selectedAssignee}
                            label="Assignee"
                            onChange={(e) => setSelectedAssignee(e.target.value as number)}
                            disabled={isAssigning}
                        >
                            {assignees.map((assignee) => (
                                <MenuItem key={assignee.id} value={assignee.id}>
                                    {assignee.fullName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Selected Items:
                    </Typography>
                    <List sx={{ 
                        maxHeight: '200px', 
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        {selectedItems.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <ListItem sx={{width: '100%'}}>
                                    <ListItemText 
                                        primary={item.workItemPath}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            noWrap: true
                                        }}
                                    />
                                </ListItem>
                                {index < selectedItems.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="warning" variant="contained" disabled={isAssigning}>
                    Cancel
                </Button>
                <Button
                    onClick={handleAssign}
                    variant="contained"
                    color="primary"
                    disabled={!selectedAssignee || isAssigning}
                    startIcon={isAssigning ? <CircularProgress size={20} color="inherit" sx={{m: 0}} /> : null}
                >
                    {isAssigning ? 'Assigning...' : 'Assign'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
